<template>
  <section id="contact" class="section contact">
    <div class="container">
      <div v-reveal class="contact__card">
        <div class="contact__intro">
          <p class="eyebrow"><span class="eyebrow__index">04</span>Contact us</p>
          <h2 class="section-title contact__title">Let’s talk about your project</h2>
          <p class="contact__text">
            You can send a&nbsp;message directly via Telegram or&nbsp;use the&nbsp;contact form so
            we can get in&nbsp;touch with you.
          </p>
          <a
            class="contact__telegram"
            href="https://t.me/formalcrypto"
            target="_blank"
            rel="noopener"
          >
            <span class="contact__telegram-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  d="M21.4 4.1L2.9 11.3c-1.3.5-1.2 1.2-.2 1.5l4.7 1.5 1.8 5.6c.2.6.4.8.9.8.4 0 .6-.2.9-.4l2.3-2.2 4.7 3.5c.9.5 1.5.2 1.7-.8l3.1-14.6c.3-1.3-.5-1.9-1.4-1.1zM9.6 14.4l-.4 4.1-1.6-5 9.8-6.2-7.8 7.1z"
                />
              </svg>
            </span>
            <span class="contact__telegram-text">
              <small>Telegram</small>
              @formalcrypto
            </span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 12l8-8M5 4h7v7" stroke="currentColor" stroke-width="1.6" />
            </svg>
          </a>
          <div class="contact__map" aria-hidden="true">
            <img src="@/assets/img/map.png" alt="" loading="lazy" />
          </div>
        </div>

        <div class="contact__form">
          <transition name="fade" mode="out-in">
            <form v-if="!sent" key="form" class="form" novalidate @submit.prevent="onSubmit">
              <div class="form__field" :class="{ 'has-error': errors.name }">
                <label class="form__label" for="contact-name">Name</label>
                <input
                  id="contact-name"
                  v-model.trim="form.name"
                  class="form__input"
                  name="name"
                  type="text"
                  autocomplete="name"
                  maxlength="80"
                  placeholder="Your name"
                  :aria-invalid="errors.name ? 'true' : 'false'"
                  aria-describedby="contact-name-error"
                  @input="errors.name = ''"
                />
                <p v-if="errors.name" id="contact-name-error" class="form__error">
                  {{ errors.name }}
                </p>
              </div>

              <div class="form__field" :class="{ 'has-error': errors.email }">
                <label class="form__label" for="contact-email">Telegram or e-mail</label>
                <input
                  id="contact-email"
                  v-model.trim="form.email"
                  class="form__input"
                  name="email"
                  type="text"
                  autocomplete="email"
                  maxlength="120"
                  placeholder="@username or e-mail"
                  :aria-invalid="errors.email ? 'true' : 'false'"
                  aria-describedby="contact-email-error"
                  @input="errors.email = ''"
                />
                <p v-if="errors.email" id="contact-email-error" class="form__error">
                  {{ errors.email }}
                </p>
              </div>

              <div class="form__field">
                <label class="form__label" for="contact-message">
                  Message <span class="form__optional">optional</span>
                </label>
                <textarea
                  id="contact-message"
                  v-model="form.message"
                  class="form__input form__textarea"
                  name="message"
                  rows="5"
                  :maxlength="maxLength"
                  placeholder="Tell us a few words about your project"
                ></textarea>
                <span class="form__counter">{{ form.message.length }} / {{ maxLength }}</span>
              </div>

              <!-- Honeypot: people never see this field, bots fill it -->
              <div class="form__hp" aria-hidden="true">
                <label for="contact-website">Website</label>
                <input
                  id="contact-website"
                  v-model="form.website"
                  name="website"
                  type="text"
                  tabindex="-1"
                  autocomplete="off"
                />
              </div>

              <div class="form__footer">
                <p v-if="serverError" class="form__server-error" role="alert">{{ serverError }}</p>
                <button
                  type="submit"
                  class="button button--primary form__submit"
                  :disabled="sending"
                >
                  <span v-if="sending" class="spinner" aria-hidden="true"></span>
                  {{ sending ? 'Sending…' : 'Send message' }}
                  <svg
                    v-if="!sending"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.6" />
                  </svg>
                </button>
              </div>
            </form>

            <div v-else key="success" class="form-success" role="status">
              <span class="form-success__icon">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                  <path d="M6 14.5l5.5 5.5L22 9" stroke="currentColor" stroke-width="2" />
                </svg>
              </span>
              <h3 class="form-success__title">Message sent</h3>
              <p class="form-success__text">
                Thanks for&nbsp;contacting us! We will get back to&nbsp;you as&nbsp;soon
                as&nbsp;possible.
              </p>
              <button type="button" class="button button--ghost" @click="reset">
                Send another message
              </button>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { getFormToken, sendTG } from '@/api'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const TELEGRAM_RE = /^(?:@|(?:https?:\/\/)?t\.me\/)?[a-z][a-z0-9_]{3,31}$/i
const PHONE_RE = /^\+?[\d\s()-]{7,20}$/
const URL_RE = /(https?:\/\/|www\.)/i

export default {
  name: 'Contact',
  data() {
    return {
      maxLength: 1000,
      sending: false,
      sent: false,
      serverError: '',
      token: '',
      form: {
        name: '',
        email: '',
        message: '',
        website: ''
      },
      errors: {
        name: '',
        email: ''
      }
    }
  },
  mounted() {
    this.loadToken()
  },
  methods: {
    async loadToken() {
      try {
        const { data } = await getFormToken()
        this.token = data.token || ''
      } catch (error) {
        this.token = ''
      }
    },
    validate() {
      const { name, email } = this.form
      this.errors.name = !name
        ? 'Please enter your name'
        : URL_RE.test(name)
        ? 'Links are not allowed here'
        : ''
      this.errors.email = !email
        ? 'Please leave your Telegram or e-mail'
        : [EMAIL_RE, TELEGRAM_RE, PHONE_RE].some(re => re.test(email))
        ? ''
        : 'Enter a valid e-mail or Telegram username'
      return !this.errors.name && !this.errors.email
    },
    async onSubmit() {
      if (this.sending || !this.validate()) {
        return
      }
      this.sending = true
      this.serverError = ''

      if (!this.token) {
        await this.loadToken()
      }

      const formData = new FormData()
      Object.keys(this.form).forEach(key => formData.append(key, this.form[key].trim()))
      formData.append('token', this.token)

      try {
        await sendTG(formData)
        this.sent = true
      } catch (error) {
        const data = error.response && error.response.data
        this.serverError =
          (data && data.message) ||
          'Something went wrong. Please try again later or write to us in Telegram.'
        if (data && data.code === 'token') {
          this.loadToken()
        }
      } finally {
        this.sending = false
      }
    },
    reset() {
      this.form.name = ''
      this.form.email = ''
      this.form.message = ''
      this.sent = false
    }
  }
}
</script>
